/**
 * **一条 / 一扇牌的布局** —— 手牌扇、旁观扇、抽牌堆共用这一份。
 *
 * 输入「几张、间距、弧度、倾角、随机角、纵向错位」，输出每一位的
 * `{ x, y, rotation }`（相对整扇原点）。**纯函数**，可在 node 里断言。
 *
 * 约定：
 * - 位次 0 在最左；`yOffset` 为正时间距向下铺（收起成竖堆），见调用方。
 * - `curvature > 0` 两端下垂（手牌扇的弧），`0` 直线（抽牌堆），
 *   `< 0` 两端上抬（倒挂）。
 * - `randomRotation` 的随机角用 `stableJitter` —— **稳定**，重排不会整扇抖。
 */
export interface ArcLayoutOptions {
  readonly count: number;
  /** 相邻两张的横向间距 */
  readonly spread: number;
  /** 弧度（占间距比例） */
  readonly curvature: number;
  /** 倾角幅度（度） */
  readonly maxTiltDeg: number;
  readonly randomRotation: boolean;
  /** 每张的纵向错位（像素） */
  readonly yOffset: number;
  /** 牌高（像素）—— 给纵向错位封顶，免得被视口切掉 */
  readonly cardHeight: number;
  /** place → 槽位下标（随机角的稳定种子用它）。省略 = 恒等 */
  readonly order?: readonly number[];
}

export interface ArcPlace {
  readonly x: number;
  readonly y: number;
  /** 弧度（不是度） */
  readonly rotation: number;
}

/** **稳定伪随机 [-1, 1)** —— 由序号决定，重排不会让牌抖一下 */
export function stableJitter(seed: number): number {
  const x = Math.sin(seed * 127.1 + 311.7) * 43758.5453;
  return (x - Math.floor(x)) * 2 - 1;
}

export function layoutArc(options: ArcLayoutOptions): ArcPlace[] {
  const n = Math.max(0, Math.floor(options.count));
  if (n === 0) return [];
  const total = options.spread * (n - 1);
  const droop = options.spread * options.curvature;
  const tiltRad = (options.maxTiltDeg * Math.PI) / 180;
  const maxTotalY = options.cardHeight * 0.5;
  const stagger = n > 1 ? Math.min(options.yOffset, maxTotalY / (n - 1)) : 0;

  const out: ArcPlace[] = [];
  for (let place = 0; place < n; place++) {
    const t = n === 1 ? 0 : place / (n - 1) - 0.5;
    const seed = options.order?.[place] ?? place;
    out.push({
      x: t * total,
      y: t * t * 4 * droop + (place - (n - 1) / 2) * stagger,
      rotation: options.randomRotation ? stableJitter(seed + 1) * tiltRad : t * tiltRad * 2,
    });
  }
  return out;
}
